import httpClient from '@/services/httpClient';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ============================================================================
// INTERFACES
// ============================================================================

export interface Project {
  id: number;
  name: string;
  description: string;
  status: 'active' | 'draft' | 'completed' | 'archived';
  maxTeams: number;
  membersPerTeam: number;
  teams?: number;
  deadline: string | null;
  startDate?: string | null;
  deliverables: number;
  instructions?: string;
  gradingCriteria?: string;
  totalPoints?: number;
  resources?: string;
  createdBy?: string;
  createdDate?: string;
  lastUpdated?: string;
}

export interface Team {
  id: number;
  teamName: string;
  teamNumber: number;
  description?: string;
  memberCount?: number;
  members?: TeamMember[];
  createdDate?: string;
}

export interface TeamMember {
  id: number;
  userId: string;
  name: string;
  email: string;
  role: 'leader' | 'member';
}

export interface Deliverable {
  id: number;
  title: string;
  displayOrder: number;
}

export interface Submission {
  id: number;
  projectId: number;
  teamId: number;
  teamName: string;
  deliverableId?: number;
  deliverableTitle?: string;
  submissionTitle: string;
  submissionContent?: string;
  submissionFileUrl?: string;
  submissionFileName?: string;
  submittedBy: string;
  submittedDate: string;
  status: 'pending' | 'submitted' | 'graded' | 'returned';
  grade?: number;
  maxGrade?: number;
  feedback?: string;
  gradedBy?: string;
  gradedDate?: string;
}

// ============================================================================
// PROJECT CRUD OPERATIONS
// ============================================================================

export const getProjects = async (filters?: {
  status?: string;
  search?: string;
}): Promise<{
  success: boolean;
  data?: Project[];
  error?: { message: string } | null;
}> => {
  try {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = `/trainer/group-projects${queryString ? `?${queryString}` : ''}`;

    const response = await httpClient.get<ApiResponse<Project[]>>(url);

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error?.message || 'Failed to fetch projects' }
    };
  }
};

export const getProjectById = async (projectId: number): Promise<{
  success: boolean;
  data?: Project;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Project>>(`/trainer/group-projects/${projectId}`);

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error?.message || 'Failed to fetch project' }
    };
  }
};

export const createProject = async (projectData: {
  name: string;
  description?: string;
  status?: 'draft' | 'active' | 'completed' | 'archived';
  maxTeams: number;
  membersPerTeam: number;
  deadline?: string;
  startDate?: string;
  instructions?: string;
  gradingCriteria?: string;
  totalPoints?: number;
}): Promise<{
  success: boolean;
  data?: Project;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<Project>>(
      '/trainer/group-projects',
      projectData
    );

    return {
      success: true,
      data: response.data,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create project'
      }
    };
  }
};

export const updateProject = async (
  projectId: number,
  projectData: Partial<Project>
): Promise<{
  success: boolean;
  data?: Project;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<Project>>(
      `/trainer/group-projects/${projectId}`,
      projectData
    );

    return {
      success: true,
      data: response.data,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update project'
      }
    };
  }
};

export const deleteProject = async (projectId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      `/trainer/group-projects/${projectId}`
    );

    return {
      success: true,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete project'
      }
    };
  }
};

// ============================================================================
// TEAM OPERATIONS
// ============================================================================

export const getTeams = async (projectId: number): Promise<{
  success: boolean;
  data?: Team[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Team[]>>(
      `/trainer/group-projects/${projectId}/teams`
    );

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error?.message || 'Failed to fetch teams' }
    };
  }
};

export const createTeam = async (
  projectId: number,
  teamData: {
    teamName: string;
    teamNumber: number;
    description?: string;
  }
): Promise<{
  success: boolean;
  data?: Team;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<Team>>(
      `/trainer/group-projects/${projectId}/teams`,
      teamData
    );

    return {
      success: true,
      data: response.data,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create team'
      }
    };
  }
};

export const updateTeam = async (
  teamId: number,
  teamData: {
    teamName: string;
    description?: string;
  }
): Promise<{
  success: boolean;
  data?: Team;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<Team>>(
      `/trainer/group-projects/teams/${teamId}`,
      teamData
    );

    return {
      success: true,
      data: response.data,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update team'
      }
    };
  }
};

export const deleteTeam = async (teamId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      `/trainer/group-projects/teams/${teamId}`
    );

    return {
      success: true,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete team'
      }
    };
  }
};

export const addTeamMember = async (
  teamId: number,
  userId: string,
  role: 'leader' | 'member' = 'member'
): Promise<{
  success: boolean;
  data?: TeamMember;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<TeamMember>>(
      `/trainer/group-projects/teams/${teamId}/members`,
      { userId, role }
    );

    return {
      success: true,
      data: response.data,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to add team member'
      }
    };
  }
};

export const removeTeamMember = async (memberId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      `/trainer/group-projects/team-members/${memberId}`
    );

    return {
      success: true,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to remove team member'
      }
    };
  }
};

// ============================================================================
// SUBMISSION OPERATIONS
// ============================================================================

export const getSubmissions = async (
  projectId: number,
  filters?: {
    teamId?: number;
    status?: string;
  }
): Promise<{
  success: boolean;
  data?: Submission[];
  error?: { message: string } | null;
}> => {
  try {
    const params = new URLSearchParams();
    if (filters?.teamId) params.append('teamId', filters.teamId.toString());
    if (filters?.status) params.append('status', filters.status);

    const queryString = params.toString();
    const url = `/trainer/group-projects/${projectId}/submissions${queryString ? `?${queryString}` : ''}`;

    const response = await httpClient.get<ApiResponse<Submission[]>>(url);

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error?.message || 'Failed to fetch submissions' }
    };
  }
};

export const submitWork = async (submissionData: {
  projectId: number;
  teamId: number;
  deliverableId?: number;
  submissionTitle: string;
  submissionContent?: string;
  submissionFileUrl?: string;
  submissionFileName?: string;
  maxGrade?: number;
}): Promise<{
  success: boolean;
  data?: Submission;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<Submission>>(
      '/trainer/group-projects/submissions',
      submissionData
    );

    return {
      success: true,
      data: response.data,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to submit work'
      }
    };
  }
};

export const gradeSubmission = async (
  submissionId: number,
  gradeData: {
    grade: number;
    feedback?: string;
    status?: 'graded' | 'returned';
  }
): Promise<{
  success: boolean;
  data?: Submission;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.patch<ApiResponse<Submission>>(
      `/trainer/group-projects/submissions/${submissionId}/grade`,
      gradeData
    );

    return {
      success: true,
      data: response.data,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to grade submission'
      }
    };
  }
};

export const deleteSubmission = async (submissionId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      `/trainer/group-projects/submissions/${submissionId}`
    );

    return {
      success: true,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete submission'
      }
    };
  }
};

// ============================================================================
// DELIVERABLE OPERATIONS
// ============================================================================

export const getDeliverables = async (projectId: number): Promise<{
  success: boolean;
  data?: Deliverable[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Deliverable[]>>(
      `/trainer/group-projects/${projectId}/deliverables`
    );

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error?.message || 'Failed to fetch deliverables' }
    };
  }
};

export const createDeliverable = async (
  projectId: number,
  deliverableData: {
    title: string;
    displayOrder?: number;
  }
): Promise<{
  success: boolean;
  data?: Deliverable;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<Deliverable>>(
      `/trainer/group-projects/${projectId}/deliverables`,
      deliverableData
    );

    return {
      success: true,
      data: response.data,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create deliverable'
      }
    };
  }
};

export const updateDeliverable = async (
  deliverableId: number,
  deliverableData: {
    title: string;
    displayOrder?: number;
  }
): Promise<{
  success: boolean;
  data?: Deliverable;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<Deliverable>>(
      `/trainer/group-projects/deliverables/${deliverableId}`,
      deliverableData
    );

    return {
      success: true,
      data: response.data,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update deliverable'
      }
    };
  }
};

export const deleteDeliverable = async (deliverableId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      `/trainer/group-projects/deliverables/${deliverableId}`
    );

    return {
      success: true,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete deliverable'
      }
    };
  }
};
